import React, { useState, useCallback, useEffect } from 'react';
import {
  Search,
  Copy,
  Download,
  Trash2,
  AlertCircle,
  CheckCircle,
  Clock,
  FileText,
  ChevronDown,
  ChevronUp,
  Loader,
  Zap,
  RefreshCw,
  Sparkles,
  Save,
  Database,
  CheckCircle2,
  ExternalLink
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { getAuth } from 'firebase/auth';

/**
 * JinaAISearch V2 - Version Corrigée et Améliorée
 * 
 * ✅ CORRECTIONS:
 * - Formatage GPT optionnel (pas de dépendance JinaAIFormat)
 * - LocalStorage sécurisé avec gestion quota
 * - Bouton sauvegarde toujours visible
 * - Auth Firebase automatique
 * 
 * ✨ AMÉLIORATIONS:
 * - UX simplifiée et intuitive
 * - Code nettoyé et optimisé
 * - Gestion d'erreurs robuste
 * - Messages clairs pour l'utilisateur
 */
const JinaAISearch = ({ 
  currentObjectiveId = 'default'
}) => {
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
  
  // 🔥 Auth automatique
  const [currentUser, setCurrentUser] = useState(null);
  
  // États de recherche
  const [searchMode, setSearchMode] = useState('url');
  const [searchUrl, setSearchUrl] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchSite, setSearchSite] = useState('');
  
  // États UI
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [results, setResults] = useState(null);
  const [rawResults, setRawResults] = useState(null);
  const [error, setError] = useState(null);
  const [searchHistory, setSearchHistory] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState(null);
  const [toast, setToast] = useState(null);
  
  // Options
  const [useGPTFormatting, setUseGPTFormatting] = useState(false); // 🔥 Désactivé par défaut
  const [autoSave, setAutoSave] = useState(false);
  const [timeout, setTimeout] = useState(45);

  // 🔥 NOUVEAU: Observer l'authentification Firebase
  useEffect(() => {
    try {
      const auth = getAuth();
      const unsubscribe = auth.onAuthStateChanged(user => {
        setCurrentUser(user);
        if (user) {
          console.log('✅ Utilisateur connecté:', user.email || user.uid);
        } else {
          console.log('⚠️ Aucun utilisateur connecté');
        }
      });
      return () => unsubscribe();
    } catch (error) {
      console.error('Erreur initialisation Firebase Auth:', error);
    }
  }, []);

  // Charger les paramètres (avec gestion d'erreurs)
  useEffect(() => {
    try {
      const saved = localStorage.getItem('jinaParams');
      if (saved) {
        const params = JSON.parse(saved);
        setUseGPTFormatting(params.useGPTFormatting || false);
        setAutoSave(params.autoSave || false);
        setTimeout(params.timeout || 45);
      }
    } catch (e) {
      console.warn('Erreur chargement paramètres:', e);
    }

    try {
      const history = localStorage.getItem('jinaHistory');
      if (history) {
        const parsed = JSON.parse(history);
        setSearchHistory(Array.isArray(parsed) ? parsed.slice(0, 10) : []); // 🔥 Limiter à 10
      }
    } catch (e) {
      console.warn('Erreur chargement historique:', e);
      // Nettoyer si corrompu
      localStorage.removeItem('jinaHistory');
    }
  }, []);

  // 🔥 AMÉLIORATION: Toast notifications
  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  // 🔥 AMÉLIORATION: Sauvegarde localStorage sécurisée
  const safeLocalStorageSet = useCallback((key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        console.warn('⚠️ Quota localStorage dépassé, nettoyage...');
        // Nettoyer l'historique ancien
        try {
          localStorage.removeItem('jinaHistory');
          localStorage.setItem(key, JSON.stringify(value));
          showToast('Historique nettoyé (quota dépassé)', 'warning');
          return true;
        } catch (e) {
          console.error('Impossible de sauvegarder:', e);
          showToast('Impossible de sauvegarder l\'historique', 'error');
          return false;
        }
      }
      return false;
    }
  }, [showToast]);

  // Sauvegarder les paramètres
  const saveParams = useCallback(() => {
    const params = { useGPTFormatting, autoSave, timeout };
    if (safeLocalStorageSet('jinaParams', params)) {
      showToast('Paramètres sauvegardés ✓');
    }
  }, [useGPTFormatting, autoSave, timeout, safeLocalStorageSet, showToast]);

  /**
   * 🔥 NOUVEAU: Formatage GPT intégré (sans dépendance externe)
   */
  const formatWithGPT = useCallback(async (content, query) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/askGPT`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: `Voici le contenu extrait d'une page web pour la question: "${query}"

CONTENU:
${content}

INSTRUCTIONS:
1. Extrais les informations pertinentes qui répondent à la question
2. Organise le contenu de manière claire et structurée
3. Élimine le bruit et les informations non pertinentes
4. Formate en Markdown lisible
5. Sois concis mais complet

RÉPONSE FORMATÉE:`
        })
      });

      if (!response.ok) throw new Error('Formatage GPT échoué');
      
      const data = await response.json();
      return data.response || content;
    } catch (error) {
      console.warn('Formatage GPT échoué, utilisation du contenu brut:', error);
      return content;
    }
  }, [API_BASE_URL]);

  /**
   * 🔥 AMÉLIORATION: Création de chunks optimisée
   */
  const createChunks = useCallback((content, metadata = {}) => {
    const CHUNK_SIZE = 1000;
    const CHUNK_OVERLAP = 200;
    
    const chunks = [];
    const lines = content.split('\n').filter(line => line.trim());
    let currentChunk = '';
    let chunkIndex = 0;
    
    for (const line of lines) {
      if (currentChunk.length + line.length > CHUNK_SIZE && currentChunk.length > 0) {
        chunks.push({
          pageContent: currentChunk.trim(),
          metadata: { ...metadata, chunkIndex: chunkIndex++, chunkSize: currentChunk.length }
        });
        
        const words = currentChunk.split(' ');
        currentChunk = words.slice(-Math.floor(CHUNK_OVERLAP / 5)).join(' ') + '\n' + line;
      } else {
        currentChunk += (currentChunk ? '\n' : '') + line;
      }
    }
    
    if (currentChunk.trim()) {
      chunks.push({
        pageContent: currentChunk.trim(),
        metadata: { ...metadata, chunkIndex: chunkIndex++, chunkSize: currentChunk.length }
      });
    }
    
    return chunks;
  }, []);

  /**
   * 🔥 AMÉLIORATION: Fonction de sauvegarde Firebase
   */
  const saveToFirebase = useCallback(async (resultToSave = null) => {
    const dataToSave = resultToSave || results;
    
    if (!dataToSave) {
      showToast('Aucun résultat à sauvegarder', 'error');
      return;
    }

    if (!currentUser) {
      showToast('Veuillez vous connecter pour sauvegarder', 'warning');
      setError('Connexion requise pour sauvegarder. Connectez-vous dans votre application.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      console.log('💾 Début sauvegarde Firebase...');

      const chunks = createChunks(dataToSave.content, {
        query: dataToSave.searchQuery || dataToSave.url,
        sourceUrl: dataToSave.url || 'search',
        extractedAt: new Date().toISOString()
      });

      console.log(`✅ ${chunks.length} chunks créés`);

      const metadata = {
        title: dataToSave.title || `Extraction: ${dataToSave.searchQuery || dataToSave.url}`,
        description: dataToSave.description || 'Extraction web via Jina AI',
        source: dataToSave.url || 'search',
        sources: dataToSave.url ? [dataToSave.url] : [],
        query: dataToSave.searchQuery || dataToSave.url,
        objectiveId: currentObjectiveId,
        userId: currentUser.uid,
        sectionCount: chunks.length,
        extractionStats: {
          responseTime: dataToSave.responseTime,
          formattedWithGPT: dataToSave.metadata?.formattedWithGPT || false,
          format: 'markdown'
        }
      };

      const token = await currentUser.getIdToken();

      const response = await fetch(`${API_BASE_URL}/api/jina/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ chunks, metadata })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Sauvegarde échouée');
      }

      console.log(`✅ Sauvegardé - Document ID: ${data.documentId}`);

      showToast(`✓ Sauvegardé ! (${data.chunkCount} chunks, ID: ${data.documentId.slice(0, 8)}...)`, 'success');
      
      setResults(prev => ({
        ...prev,
        _savedToFirebase: true,
        _documentId: data.documentId
      }));

      return data;

    } catch (error) {
      console.error('❌ Erreur sauvegarde:', error);
      showToast(`Échec: ${error.message}`, 'error');
      setError(`Sauvegarde échouée: ${error.message}`);
      return null;
    } finally {
      setSaving(false);
    }
  }, [results, currentUser, currentObjectiveId, createChunks, API_BASE_URL, showToast]);

  /**
   * Extraction d'URL
   */
  const performURLExtraction = useCallback(async () => {
    if (!searchUrl.trim()) {
      setError('Veuillez entrer une URL');
      return;
    }

    try {
      new URL(searchUrl);
    } catch (e) {
      setError('URL invalide (ex: https://example.com)');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);
    setRawResults(null);

    try {
      const startTime = Date.now();

      const response = await fetch(`${API_BASE_URL}/api/jina/extract`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: searchUrl, timeout })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Extraction échouée');
      }

      const responseTime = Date.now() - startTime;
      const rawContent = data.content || '';

      setRawResults({
        url: searchUrl,
        content: rawContent,
        title: data.title || 'Sans titre',
        responseTime,
        metadata: data.metadata || {}
      });

      let formattedContent = rawContent;
      if (useGPTFormatting && rawContent.length > 0) {
        showToast('Formatage GPT en cours...', 'info');
        formattedContent = await formatWithGPT(rawContent, `Extrais le contenu de ${searchUrl}`);
      }

      const resultData = {
        url: searchUrl,
        title: data.title || 'Sans titre',
        description: data.description || '',
        content: formattedContent,
        responseTime,
        format: 'markdown',
        timestamp: new Date().toISOString(),
        metadata: {
          hasRawContent: !!rawContent,
          formattedWithGPT: useGPTFormatting,
          author: data.author,
          publishedTime: data.publishedTime
        }
      };

      setResults(resultData);
      showToast('✓ Extraction réussie !');

      // Auto-sauvegarde
      if (autoSave && currentUser) {
        console.log('🔄 Auto-sauvegarde...');
        await saveToFirebase(resultData);
      }

      // Historique (limité à 10)
      const newHistory = [
        {
          id: Date.now(),
          url: searchUrl,
          status: 'success',
          timestamp: new Date().toISOString(),
          title: resultData.title,
          _savedToFirebase: autoSave && currentUser
        },
        ...searchHistory
      ].slice(0, 10); // 🔥 Limiter à 10

      setSearchHistory(newHistory);
      safeLocalStorageSet('jinaHistory', newHistory);

    } catch (error) {
      console.error('Erreur extraction:', error);
      setError(error.message);
      showToast(`Erreur: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [searchUrl, timeout, useGPTFormatting, autoSave, currentUser, searchHistory, 
      API_BASE_URL, formatWithGPT, saveToFirebase, safeLocalStorageSet, showToast]);

  /**
   * Recherche web
   */
  const performSearch = useCallback(async () => {
    if (!searchQuery.trim()) {
      setError('Veuillez entrer une requête');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);
    setRawResults(null);

    try {
      const startTime = Date.now();

      const response = await fetch(`${API_BASE_URL}/api/jina/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: searchQuery,
          site: searchSite.trim() || undefined
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Recherche échouée');
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Recherche échouée');
      }

      const responseTime = Date.now() - startTime;
      const rawContent = data.content || '';

      setRawResults({
        query: searchQuery,
        site: searchSite,
        content: rawContent,
        responseTime,
        metadata: data.metadata || {}
      });

      let formattedContent = rawContent;
      if (useGPTFormatting && rawContent.length > 0) {
        showToast('Formatage GPT en cours...', 'info');
        formattedContent = await formatWithGPT(rawContent, searchQuery);
      }

      const resultData = {
        searchQuery,
        searchSite: searchSite || 'Tous les domaines',
        title: `Résultats: ${searchQuery}`,
        description: `Recherche ${searchSite ? `sur ${searchSite}` : 'web'}`,
        content: formattedContent,
        responseTime,
        format: 'markdown',
        timestamp: new Date().toISOString(),
        metadata: {
          hasRawContent: !!rawContent,
          formattedWithGPT: useGPTFormatting,
          ...data.metadata
        }
      };

      setResults(resultData);
      showToast('✓ Recherche réussie !');

      // Auto-sauvegarde
      if (autoSave && currentUser) {
        await saveToFirebase(resultData);
      }

      // Historique
      const newHistory = [
        {
          id: Date.now(),
          query: searchQuery,
          site: searchSite,
          status: 'success',
          timestamp: new Date().toISOString(),
          title: resultData.title,
          _savedToFirebase: autoSave && currentUser
        },
        ...searchHistory
      ].slice(0, 10);

      setSearchHistory(newHistory);
      safeLocalStorageSet('jinaHistory', newHistory);

    } catch (error) {
      console.error('Erreur recherche:', error);
      setError(error.message);
      showToast(`Erreur: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, searchSite, useGPTFormatting, autoSave, currentUser, searchHistory,
      API_BASE_URL, formatWithGPT, saveToFirebase, safeLocalStorageSet, showToast]);

  // Utilitaires
  const toggleRawContent = () => {
    if (results && rawResults) {
      setResults(prev => ({
        ...prev,
        content: prev._showingRaw ? results.content : rawResults.content,
        _showingRaw: !prev._showingRaw
      }));
    }
  };

  const loadFromHistory = (item) => {
    if (item.url) {
      setSearchMode('url');
      setSearchUrl(item.url);
    } else if (item.query) {
      setSearchMode('query');
      setSearchQuery(item.query);
      setSearchSite(item.site || '');
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(results.content);
      showToast('✓ Contenu copié');
    } catch (e) {
      showToast('Échec de la copie', 'error');
    }
  };

  const downloadContent = () => {
    const element = document.createElement('a');
    const file = new Blob([results.content], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `jina-${Date.now()}.md`;
    element.click();
    showToast('✓ Téléchargement démarré');
  };

  const clearHistory = () => {
    if (window.confirm('Effacer tout l\'historique ?')) {
      setSearchHistory([]);
      localStorage.removeItem('jinaHistory');
      showToast('Historique effacé');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 pb-12">
      <div className="max-w-6xl mx-auto px-4 py-8">
        
        {/* HEADER */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Zap className="w-10 h-10 text-blue-600" />
              <h1 className="text-4xl font-bold text-slate-900">Jina AI Search</h1>
            </div>
            
            {/* Badges */}
            <div className="flex items-center gap-2">
              {useGPTFormatting && (
                <div className="flex items-center gap-1 bg-purple-100 px-3 py-1 rounded-full">
                  <Sparkles className="w-4 h-4 text-purple-600" />
                  <span className="text-sm text-purple-700 font-medium">GPT</span>
                </div>
              )}
              {autoSave && currentUser && (
                <div className="flex items-center gap-1 bg-green-100 px-3 py-1 rounded-full">
                  <Database className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-700 font-medium">Auto-save</span>
                </div>
              )}
              {currentUser && (
                <div className="flex items-center gap-1 bg-blue-100 px-3 py-1 rounded-full">
                  <CheckCircle className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-blue-700 font-medium">
                    {currentUser.email?.split('@')[0] || 'Connecté'}
                  </span>
                </div>
              )}
              {!currentUser && (
                <div className="flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full">
                  <AlertCircle className="w-4 h-4 text-gray-600" />
                  <span className="text-sm text-gray-700 font-medium">Non connecté</span>
                </div>
              )}
            </div>
          </div>
          <p className="text-slate-600">
            Extraire et rechercher du contenu web avec formatage intelligent
          </p>
        </div>

        {/* TOAST NOTIFICATIONS */}
        {toast && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 animate-in fade-in slide-in-from-top-2 ${
            toast.type === 'success' ? 'bg-green-50 border border-green-200' :
            toast.type === 'error' ? 'bg-red-50 border border-red-200' :
            toast.type === 'warning' ? 'bg-yellow-50 border border-yellow-200' :
            'bg-blue-50 border border-blue-200'
          }`}>
            {toast.type === 'success' && <CheckCircle className="w-5 h-5 text-green-600" />}
            {toast.type === 'error' && <AlertCircle className="w-5 h-5 text-red-600" />}
            {toast.type === 'warning' && <AlertCircle className="w-5 h-5 text-yellow-600" />}
            {toast.type === 'info' && <Loader className="w-5 h-5 text-blue-600 animate-spin" />}
            <span className={`text-sm font-medium ${
              toast.type === 'success' ? 'text-green-700' :
              toast.type === 'error' ? 'text-red-700' :
              toast.type === 'warning' ? 'text-yellow-700' :
              'text-blue-700'
            }`}>
              {toast.message}
            </span>
          </div>
        )}

        {/* ERROR MESSAGE */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-red-700 mb-1">Erreur</p>
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        )}

        {/* FORMULAIRE */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          
          {/* Mode Tabs */}
          <div className="mb-6 flex gap-4">
            <button
              onClick={() => setSearchMode('url')}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition ${
                searchMode === 'url'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <FileText className="w-5 h-5" />
              Extraire URL
            </button>
            <button
              onClick={() => setSearchMode('query')}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition ${
                searchMode === 'query'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <Search className="w-5 h-5" />
              Rechercher
            </button>
          </div>

          {/* MODE URL */}
          {searchMode === 'url' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  URL à extraire
                </label>
                <input
                  type="url"
                  value={searchUrl}
                  onChange={(e) => setSearchUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  onKeyPress={(e) => e.key === 'Enter' && performURLExtraction()}
                />
              </div>

              <button
                onClick={performURLExtraction}
                disabled={loading || !searchUrl.trim()}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 text-white font-semibold py-3 rounded-lg transition flex items-center justify-center gap-2"
              >
                {loading && <Loader className="w-5 h-5 animate-spin" />}
                {loading ? 'Extraction en cours...' : 'Extraire'}
              </button>
            </div>
          )}

          {/* MODE SEARCH */}
          {searchMode === 'query' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Requête de recherche
                </label>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="ex: tarifs formation Python"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  onKeyPress={(e) => e.key === 'Enter' && performSearch()}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Site spécifique (optionnel)
                </label>
                <input
                  type="text"
                  value={searchSite}
                  onChange={(e) => setSearchSite(e.target.value)}
                  placeholder="ex: python.org"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <button
                onClick={performSearch}
                disabled={loading || !searchQuery.trim()}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 text-white font-semibold py-3 rounded-lg transition flex items-center justify-center gap-2"
              >
                {loading && <Loader className="w-5 h-5 animate-spin" />}
                {loading ? 'Recherche en cours...' : 'Rechercher'}
              </button>
            </div>
          )}

          {/* OPTIONS AVANCÉES */}
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="mt-6 text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-2 text-sm"
          >
            {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Options avancées
          </button>

          {showAdvanced && (
            <div className="mt-6 pt-6 border-t border-slate-200 space-y-4">
              
              {/* Formatage GPT */}
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useGPTFormatting}
                    onChange={(e) => setUseGPTFormatting(e.target.checked)}
                    className="mt-1 w-4 h-4"
                  />
                  <div className="flex-1">
                    <span className="font-semibold text-slate-700 flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-purple-600" />
                      Formatage GPT
                    </span>
                    <p className="text-xs text-slate-600 mt-1">
                      Utiliser GPT pour formater et améliorer les résultats (ralentit l'extraction)
                    </p>
                  </div>
                </label>
              </div>

              {/* Auto-sauvegarde */}
              <div className={`p-4 rounded-lg border ${
                currentUser 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <label className={`flex items-start gap-3 ${currentUser ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                  <input
                    type="checkbox"
                    checked={autoSave}
                    onChange={(e) => setAutoSave(e.target.checked)}
                    disabled={!currentUser}
                    className="mt-1 w-4 h-4"
                  />
                  <div className="flex-1">
                    <span className="font-semibold text-slate-700 flex items-center gap-2">
                      <Database className={`w-4 h-4 ${currentUser ? 'text-green-600' : 'text-gray-400'}`} />
                      Auto-sauvegarde Firebase
                      {!currentUser && <span className="text-xs text-red-600">(Connexion requise)</span>}
                    </span>
                    <p className="text-xs text-slate-600 mt-1">
                      Sauvegarder automatiquement chaque extraction dans Firebase + FAISS
                    </p>
                  </div>
                </label>
              </div>

              {/* Timeout */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Timeout (secondes)
                </label>
                <input
                  type="number"
                  value={timeout}
                  onChange={(e) => setTimeout(parseInt(e.target.value) || 45)}
                  min="10"
                  max="120"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                />
              </div>

              <button
                onClick={saveParams}
                className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-2 rounded-lg transition"
              >
                Sauvegarder les paramètres
              </button>
            </div>
          )}
        </div>

        {/* RÉSULTATS */}
        {loading && (
          <div className="bg-white rounded-lg shadow-lg p-12 text-center">
            <Loader className="w-12 h-12 text-blue-600 mx-auto mb-4 animate-spin" />
            <p className="text-slate-600 text-lg">
              {useGPTFormatting ? 'Extraction et formatage en cours...' : 'Extraction en cours...'}
            </p>
          </div>
        )}

        {results && !loading && (
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
              <div className="flex items-start justify-between mb-3">
                <h2 className="text-2xl font-bold flex-1">{results.title}</h2>
                
                {/* 🔥 BOUTON SAUVEGARDER - TOUJOURS VISIBLE */}
                <button
                  onClick={() => saveToFirebase()}
                  disabled={saving || !currentUser || results._savedToFirebase}
                  title={
                    !currentUser ? 'Connectez-vous pour sauvegarder' :
                    results._savedToFirebase ? 'Déjà sauvegardé' :
                    'Sauvegarder dans Firebase + FAISS'
                  }
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition ${
                    results._savedToFirebase
                      ? 'bg-green-500 text-white cursor-default'
                      : !currentUser
                      ? 'bg-white/20 text-white/60 cursor-not-allowed'
                      : 'bg-white/90 hover:bg-white text-blue-600 shadow-lg hover:shadow-xl'
                  }`}
                >
                  {saving ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Sauvegarde...</span>
                    </>
                  ) : results._savedToFirebase ? (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      <span className="text-sm">Sauvegardé</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      <span className="text-sm">Sauvegarder</span>
                    </>
                  )}
                </button>
              </div>
              
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-blue-100 text-xs">Temps</p>
                  <p className="text-lg font-semibold flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {results.responseTime}ms
                  </p>
                </div>
                <div>
                  <p className="text-blue-100 text-xs">Format</p>
                  <p className="text-lg font-semibold">{results.format}</p>
                </div>
                {results.metadata?.formattedWithGPT && (
                  <div>
                    <p className="text-blue-100 text-xs">Formatage</p>
                    <p className="text-lg font-semibold flex items-center gap-1">
                      <Sparkles className="w-4 h-4" />
                      GPT
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            {results.description && (
              <div className="border-b border-slate-200 p-6">
                <p className="text-slate-600 text-sm">{results.description}</p>
              </div>
            )}

            {/* Contenu */}
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Contenu {results._showingRaw ? '(brut)' : '(formaté)'}
                </h4>
                
                <div className="flex gap-2">
                  {rawResults && (
                    <button
                      onClick={toggleRawContent}
                      title={results._showingRaw ? "Voir formaté" : "Voir brut"}
                      className="p-2 hover:bg-slate-100 rounded-lg transition"
                    >
                      <RefreshCw className="w-5 h-5 text-slate-600" />
                    </button>
                  )}
                  <button
                    onClick={copyToClipboard}
                    className="p-2 hover:bg-slate-100 rounded-lg transition"
                    title="Copier"
                  >
                    <Copy className="w-5 h-5 text-slate-600" />
                  </button>
                  <button
                    onClick={downloadContent}
                    className="p-2 hover:bg-slate-100 rounded-lg transition"
                    title="Télécharger"
                  >
                    <Download className="w-5 h-5 text-slate-600" />
                  </button>
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-6 max-h-96 overflow-y-auto">
                <ReactMarkdown 
                  components={{
                    a: ({node, ...props}) => (
                      <a {...props} target="_blank" rel="noopener noreferrer" 
                         className="text-blue-600 hover:underline inline-flex items-center gap-1">
                        {props.children}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ),
                    code: ({node, inline, ...props}) => (
                      <code {...props} className={inline 
                        ? 'bg-gray-200 px-2 py-1 rounded text-sm' 
                        : 'bg-gray-800 text-gray-100 p-4 block rounded overflow-x-auto text-sm'
                      } />
                    ),
                    h1: ({node, ...props}) => <h1 {...props} className="text-3xl font-bold mt-6 mb-3" />,
                    h2: ({node, ...props}) => <h2 {...props} className="text-2xl font-bold mt-5 mb-2" />,
                    h3: ({node, ...props}) => <h3 {...props} className="text-xl font-bold mt-4 mb-2" />,
                    ul: ({node, ...props}) => <ul {...props} className="list-disc list-inside mb-3 space-y-1" />,
                    ol: ({node, ...props}) => <ol {...props} className="list-decimal list-inside mb-3 space-y-1" />,
                    p: ({node, ...props}) => <p {...props} className="mb-3 leading-relaxed" />
                  }}
                >
                  {results.content}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        )}

        {/* HISTORIQUE */}
        {searchHistory.length > 0 && (
          <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Clock className="w-6 h-6 text-slate-600" />
                Historique ({searchHistory.length}/10)
              </h3>
              <button
                onClick={clearHistory}
                className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1"
              >
                <Trash2 className="w-4 h-4" />
                Effacer
              </button>
            </div>

            <div className="space-y-2">
              {searchHistory.map((item) => (
                <div
                  key={item.id}
                  className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition cursor-pointer"
                  onClick={() => setExpandedHistory(expandedHistory === item.id ? null : item.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {item._savedToFirebase && (
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                      )}
                      <p className="text-sm font-semibold text-slate-900 truncate">
                        {item.title || item.url || item.query}
                      </p>
                    </div>
                    <p className="text-xs text-slate-500 ml-3">
                      {new Date(item.timestamp).toLocaleString('fr-FR', {
                        day: '2-digit',
                        month: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>

                  {expandedHistory === item.id && (
                    <div className="mt-3 pt-3 border-t border-slate-200 flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          loadFromHistory(item);
                        }}
                        className="flex-1 text-sm bg-blue-50 hover:bg-blue-100 text-blue-700 font-semibold py-2 rounded transition"
                      >
                        Réutiliser
                      </button>
                      {currentUser && !item._savedToFirebase && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Charger et sauvegarder
                            loadFromHistory(item);
                            setTimeout(() => saveToFirebase(), 100);
                          }}
                          className="text-sm bg-green-50 hover:bg-green-100 text-green-700 font-semibold py-2 px-4 rounded transition flex items-center gap-1"
                        >
                          <Save className="w-4 h-4" />
                          Sauvegarder
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ÉTAT VIDE */}
        {!loading && !results && !error && (
          <div className="bg-white rounded-lg shadow-lg p-12 text-center">
            <Search className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 text-lg mb-2">
              Commencez par entrer une URL ou une requête
            </p>
            {!currentUser && (
              <p className="text-sm text-amber-600 flex items-center justify-center gap-1 mt-3">
                <AlertCircle className="w-4 h-4" />
                Connectez-vous pour activer la sauvegarde
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default JinaAISearch;